import { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { ImageIcon, Play, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const ThumbnailSection = ({ workout, updateField, token }) => {
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const thumbInputRef = useRef(null);

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExt = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !allowedExt.includes(fileExt)) {
      toast.error('Invalid image type. Use JPG, PNG, or WebP.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum 10MB.');
      return;
    }

    setUploadingThumb(true);
    const apiBase = API || window.location.origin;
    try {
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      const resp = await fetch(`${apiBase}/api/admin/workouts/${workout.id}/thumbnail`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, data: base64Data }),
      });
      if (!resp.ok) throw new Error(`Upload failed: ${resp.status}`);
      const res = await resp.json();
      updateField('video_thumbnail', res.thumbnail_url);
      toast.success('Thumbnail uploaded!');
    } catch (err) {
      toast.error(err.message || 'Failed to upload thumbnail.');
    } finally {
      setUploadingThumb(false);
      if (thumbInputRef.current) thumbInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-black border border-border p-4 rounded" data-testid="thumbnail-section">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-white uppercase tracking-wider text-sm">
          <ImageIcon className="inline h-4 w-4 mr-2" />
          Video Cover / Thumbnail
        </Label>
      </div>

      {workout.video_thumbnail ? (
        <div className="space-y-3">
          <div className="relative w-full h-40 rounded overflow-hidden">
            <img
              src={workout.video_thumbnail.startsWith('http') ? workout.video_thumbnail : `${API}${workout.video_thumbnail}`}
              alt="Video thumbnail"
              className="w-full h-full object-cover"
              data-testid="video-thumbnail-preview"
            />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
              <Play className="h-10 w-10 text-white" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => thumbInputRef.current?.click()}
              disabled={uploadingThumb}
              className="border-border text-white"
              data-testid="change-thumbnail-btn"
            >
              {uploadingThumb ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ImageIcon className="h-4 w-4 mr-1" />}
              Change Cover
            </Button>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => updateField('video_thumbnail', '')}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid="remove-thumbnail-btn"
            >
              <X className="h-4 w-4 mr-1" /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <div
          className="border-2 border-dashed border-border hover:border-primary/50 rounded-lg p-4 text-center cursor-pointer transition-colors"
          onClick={() => !uploadingThumb && thumbInputRef.current?.click()}
          data-testid="thumbnail-upload-dropzone"
        >
          {uploadingThumb ? (
            <Loader2 className="h-6 w-6 text-primary animate-spin mx-auto" />
          ) : (
            <>
              <ImageIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-white text-sm font-medium">Upload a cover image</p>
              <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP (max 10MB)</p>
            </>
          )}
        </div>
      )}

      <input
        ref={thumbInputRef}
        type="file"
        accept="image/*"
        onChange={handleThumbnailUpload}
        className="hidden"
        data-testid="thumbnail-upload-input"
      />
    </div>
  );
};
